// The timeline's graph, as numbers.
//
// Story RO-021. `agent_runtime_lld.md` §30.13.3. The same shape as
// Apex-Concierge's observatory graph: one lane per kind, turns as bands,
// ticks at a readable step, a `now` line while the stream is live.
//
// No DOM here, for the reason `model.js` has none: the decisions — where a
// bar goes, how wide a second is, which tick is labelled — are tested in
// Node, and the component only draws what this returns.

/** The lanes, in the order they are drawn. */
export const LANES = [
  { kind: "message", label: "Messages", family: "message" },
  { kind: "inference", label: "Inference", family: "inference" },
  { kind: "tool", label: "Tools", family: "tool" },
  { kind: "response", label: "Responses", family: "response" },
  { kind: "child", label: "Sub-agents", family: "child_agent" },
  { kind: "error", label: "Errors", family: "error" },
];

export const LABEL_WIDTH = 92;
export const LANE_HEIGHT = 30;
export const AXIS_HEIGHT = 22;
export const BAR_HEIGHT = 14;
const RIGHT_PAD = 16;
const MIN_BAR = 3;
const TICK_STEPS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600];
const MIN_TICK_PX = 80;

/**
 * @param {{ items: object[], start: number, end: number, now?: number,
 *           width: number }} input
 *   `start` is the session's first frame; `now`, when the stream is live,
 *   extends the window to the present.
 */
export function layoutGraph({ items, start, end, now, width }) {
  // The window opens a moment before the first item rather than at the
  // session's first frame: a session's agent is created when it is opened,
  // and the work may begin much later. Ticks still read from the session's
  // start, so a bar's position agrees with the cards' +Δ.
  const timed = items.filter((item) => item.start !== undefined).map((item) => item.start);
  const from = timed.length ? Math.max(start, Math.min(...timed) - 1000) : start;
  // A lane is drawn when something is in it, and the three every turn has
  // are always drawn, so an idle session still shows where work would go.
  const used = new Set(items.map((item) => item.kind));
  const lanes = LANES.filter(
    (lane) => used.has(lane.kind) || ["inference", "tool", "response"].includes(lane.kind),
  ).map((lane, index) => ({ ...lane, y: AXIS_HEIGHT + index * LANE_HEIGHT }));
  const laneOf = new Map(lanes.map((lane) => [lane.kind, lane]));

  const last = Math.max(end ?? start, now ?? start);
  const spanSec = Math.max((last - from) / 1000, 1);
  const usable = Math.max(width - LABEL_WIDTH - RIGHT_PAD, 120);
  // Fitted to the width down to 3 px a second, so a phone sees a five-minute
  // session whole and scrolls only past that.
  const pxPerSec = Math.min(Math.max(usable / spanSec, 3), 400);
  const plotWidth = LABEL_WIDTH + spanSec * pxPerSec + RIGHT_PAD;
  const x = (at) => LABEL_WIDTH + ((at - from) / 1000) * pxPerSec;

  const bands = items
    .filter((item) => item.kind === "turn")
    .map((item, index) => ({
      id: item.id,
      x: x(item.start),
      w: Math.max(x(item.end ?? last) - x(item.start), 1),
      even: index % 2 === 0,
    }));

  const marks = items
    .filter((item) => laneOf.has(item.kind) && item.start !== undefined)
    .map((item) => {
      const lane = laneOf.get(item.kind);
      const running = item.end === undefined;
      const until = item.end ?? last;
      const dot = !running && item.end === item.start;
      const left = x(item.start);
      return {
        id: item.id,
        kind: item.kind,
        family: item.family,
        agentId: item.agentId,
        x: left,
        y: lane.y + (LANE_HEIGHT - BAR_HEIGHT) / 2,
        w: dot ? BAR_HEIGHT : Math.max(x(until) - left, MIN_BAR),
        h: BAR_HEIGHT,
        dot,
        running,
        failed: item.state === "failed",
      };
    });

  const step = TICK_STEPS.find((candidate) => candidate * pxPerSec >= MIN_TICK_PX) ?? TICK_STEPS.at(-1);
  const ticks = [];
  const offset = (from - start) / 1000;
  const firstTick = Math.ceil(offset / step) * step;
  for (let sec = firstTick; sec <= offset + spanSec + 1e-9; sec += step) {
    ticks.push({ x: LABEL_WIDTH + (sec - offset) * pxPerSec, label: tickLabel(sec) });
  }

  return {
    lanes,
    bands,
    marks,
    ticks,
    nowX: now === undefined ? undefined : x(now),
    width: Math.max(plotWidth, width),
    height: AXIS_HEIGHT + lanes.length * LANE_HEIGHT + 6,
    pxPerSec,
  };
}

/** `0s`, `5s`, `1:30`. */
export function tickLabel(sec) {
  if (sec < 60) return `${Math.round(sec)}s`;
  const total = Math.round(sec);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}
